import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { childrenKeys } from "~/api/children/keys";
import { getAuth, isProfileAuth } from "~/providers/store/auth";
import { DisplayError, DisplayErrorCodeEnum } from "~/utils/display-error";
import { isApiError } from "~/api/error";
import { toast } from "~/components/hooks/use-toast";
import { useT } from "@repo/transifex";
import { collectActions } from "~/providers/store/collect/actions";
import { useCollectFlow } from "~/providers/store/collect/hooks";
import { employeeActions, useEmployee } from "~/providers/store/employee";
import { basketActions } from "~/providers/store/basket";

import { collectOrderLines, type CollectOrderLinesArgs } from "../request";
import { collectOrderKeys } from "../keys";

type UseCollectOrderLinesArgs = Pick<
  CollectOrderLinesArgs,
  "childId" | "orderLines"
>;

export function useCollectOrderLines() {
  const t = useT();

  return useMutation({
    mutationFn: async ({ childId, orderLines }: UseCollectOrderLinesArgs) => {
      const auth = getAuth();
      if (!isProfileAuth(auth)) {
        throw new DisplayError(DisplayErrorCodeEnum.PROFILE_NOT_SELECTED);
      }

      return collectOrderLines({
        tillId: auth.tillId,
        childId,
        profileId: auth.profileId,
        orderLines
      });
    },
    onError: (error) => {
      const errorMessage = isApiError(error)
        ? error.body.message
        : t("Could not collect order");

      toast({
        title: t("Collect failed"),
        variant: "destructive",
        description: errorMessage
      });
    }
  });
}

type UseCollectSuccessHandlerArgs = {
  collectedOrderLines: number;
  isFullyCollected: boolean;
  additionalProductsPurchased?: number;
  forceRedirect?: boolean;
};

export const useCollectSuccessHandler = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const t = useT();
  const collectFlow = useCollectFlow();
  const { employeeChildId } = useEmployee();

  return ({
    collectedOrderLines,
    isFullyCollected,
    additionalProductsPurchased,
    forceRedirect
  }: UseCollectSuccessHandlerArgs) => {
    if (!employeeChildId) {
      throw new Error("User not found");
    }

    const redirectTo =
      collectFlow === "collect" || collectFlow === "collect-pos"
        ? "/collect"
        : "/pos/sell";

    // Refresh the students list and this order after a collection.
    queryClient.invalidateQueries({
      queryKey: childrenKeys.lists()
    });
    queryClient.invalidateQueries({
      queryKey: collectOrderKeys.detail({ childId: employeeChildId })
    });

    let extraMessage = "";
    if (additionalProductsPurchased) {
      extraMessage = t(" and {count} additional {products} purchased", {
        count: additionalProductsPurchased,
        products:
          additionalProductsPurchased === 1 ? t("product") : t("products")
      });
    }

    const collectedMessage = isFullyCollected
      ? t("Order collected")
      : t("{count} {items} collected", {
          count: collectedOrderLines,
          items: collectedOrderLines === 1 ? t("item") : t("items")
        });

    toast({
      title: `${collectedMessage}${extraMessage}`,
      variant: "success"
    });

    if (!isFullyCollected || forceRedirect) {
      navigate(redirectTo, { replace: true });
      collectActions.reset();
      collectActions.resetFilters();
    }

    employeeActions.reset();
    basketActions.reset();
  };
};
